import axios from "axios";

// Environment-based configuration
export const BaseURL = process.env.REACT_APP_BASE_URL || "http://localhost:8888/";
export const BucketURL = process.env.REACT_APP_BUCKET_URL || "https://ppsk.fra1.digitaloceanspaces.com/";

// Request timeout
const REQUEST_TIMEOUT = 30000;

// Request cancellation token storage
const pendingRequests = new Map();

// Create axios instance with default config for better performance
const axiosInstance = axios.create({
    baseURL: BaseURL,
    timeout: REQUEST_TIMEOUT,
});

// Request interceptor to handle cancellation
axiosInstance.interceptors.request.use((config) => {
    // Generate unique key for this request
    const requestKey = `${config.method}_${config.url}_${JSON.stringify(config.data || {})}`;
    
    // Cancel previous request with same key if exists
    if (pendingRequests.has(requestKey)) {
        const controller = pendingRequests.get(requestKey);
        controller.abort();
    }
    
    // Create new abort controller for this request
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(requestKey, controller);
    
    // Store key in config for cleanup
    config.requestKey = requestKey;
    
    return config;
}, (error) => Promise.reject(error));

// Response interceptor to cleanup
axiosInstance.interceptors.response.use((response) => {
    // Remove from pending requests
    if (response.config.requestKey) {
        pendingRequests.delete(response.config.requestKey);
    }
    return response;
}, (error) => {
    // Remove from pending requests
    if (error.config?.requestKey) {
        pendingRequests.delete(error.config.requestKey);
    }
    
    // Don't throw error for cancelled requests
    if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return Promise.reject({ cancelled: true });
    }
    
    return Promise.reject(error);
});

// Helper to cancel all pending requests (useful on page navigation)
export const cancelAllPendingRequests = () => {
    pendingRequests.forEach((controller) => {
        controller.abort();
    });
    pendingRequests.clear();
};


export const ApiGetNoAuth = (type) => {
    return new Promise((resolve, reject) => {
        axios
            .get(BaseURL + type)
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (
                    error?.hasOwnProperty("response") &&
                    error?.response?.hasOwnProperty("data") &&
                    error?.response?.data?.hasOwnProperty("error") &&
                    error?.response?.data?.error
                ) {
                    reject(error?.response?.data);
                } else {
                    reject(error?.response);
                }
            });
    });
};

export const ApiPostNoAuth = (type, userData) => {
    return new Promise((resolve, reject) => {
        axios
            .post(BaseURL + type, userData)
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (
                    error?.hasOwnProperty("response") &&
                    error?.response?.hasOwnProperty("data") &&
                    error?.response?.data?.hasOwnProperty("error") &&
                    error?.response?.data?.error
                ) {
                    reject(error?.response?.data);
                } else {
                    reject(error?.response);
                }
            });
    });
};

export const ApiPutNoAuth = (type, userData) => {
    return new Promise((resolve, reject) => {
        axios
            .put(BaseURL + type, userData)
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (
                    error?.hasOwnProperty("response") &&
                    error?.response?.hasOwnProperty("data") &&
                    error?.response?.data?.hasOwnProperty("error") &&
                    error?.response?.data?.error
                ) {
                    reject(error?.response?.data);
                } else {
                    reject(error?.response);
                }
            });
    });
};

// Shared error handler for authenticated API calls
const handleAuthApiError = (error, reject) => {
    if (error?.cancelled) {
        return { cancelled: true };
    }
    if (
        error?.hasOwnProperty("response") &&
        error?.response?.hasOwnProperty("data") &&
        error?.response?.data?.hasOwnProperty("error") &&
        error?.response?.data?.error
    ) {
        if (error?.response?.status === 403) {
            reject(error?.response?.data);
            return;
        }
        reject(error?.response?.data);
        return;
    }
    reject(error);
    if (error?.response?.data?.message === "Token has expired") {
        localStorage.clear();
    } else if (error?.response?.data?.message === "Too Many Attempts.") {
        alert("Please wait 2 minutes, then reload the website.");
    }
};

export const ApiDeleteNoAuth = (type) => {
    return new Promise((resolve, reject) => {
        axios
            .delete(BaseURL + type)
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (
                    error?.hasOwnProperty("response") &&
                    error?.response?.hasOwnProperty("data") &&
                    error?.response?.data?.hasOwnProperty("error") &&
                    error?.response?.data?.error
                ) {
                    reject(error?.response?.data);
                } else {
                    reject(error?.response);
                }
            });
    });
};

export const ApiGet = (type) => {
    return new Promise((resolve, reject) => {
        axiosInstance
            .get(type, {
                headers: {
                    Authorization: localStorage.getItem("auth_token"),
                },
            })
            .then((responseJson) => {
                console.log(`[ApiGet] ${type} success:`, responseJson?.data?.data);
                resolve(responseJson);
            })
            .catch((error) => {
                // Ignore cancelled requests
                if (error?.cancelled) {
                    console.log(`[ApiGet] ${type} cancelled`);
                    return resolve({ cancelled: true });
                }
                console.error(`[ApiGet] ${type} error:`, error);
                if (
                    error?.hasOwnProperty("response") &&
                    error?.response?.hasOwnProperty("data") &&
                    error?.response?.data?.hasOwnProperty("error") &&
                    error?.response?.data?.error
                ) {
                    reject(error?.response?.data);
                } else {
                    reject(error);
                    if (error?.response?.data?.message === "Token has expired") {
                        localStorage.clear();
                    }
                }
            });
    });
};

export const ApiGetBlob = (type) => {
    return new Promise((resolve, reject) => {
        axiosInstance
            .get(type, {
                headers: {
                    Authorization: localStorage.getItem("auth_token"),
                },
                responseType: "blob",
                timeout: 120000,
            })
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                if (error?.cancelled) {
                    return resolve({ cancelled: true });
                }
                reject(error);
            });
    });
};

export const ApiPost = (type, userData) => {
    return new Promise((resolve, reject) => {
        axiosInstance
            .post(type, userData, {
                headers: {
                    Authorization: localStorage.getItem("auth_token"),
                },
            })
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                // Ignore cancelled requests
                if (error?.cancelled) {
                    return resolve({ cancelled: true });
                }
                if (
                    error?.hasOwnProperty("response") &&
                    error?.response?.hasOwnProperty("data") &&
                    error?.response?.data?.hasOwnProperty("error") &&
                    error?.response?.data?.error
                ) {
                    if (error?.response?.status === 403) {
                        reject(error?.response?.data);
                    } else {
                        reject(error?.response?.data);
                    }
                } else {
                    reject(error);
                    if (error?.response?.data?.message === "Token has expired") {
                        localStorage.clear();
                    } else if (error?.response?.data?.message === "Too Many Attempts.") {
                        alert("Please wait 2 minute, after 2 minute reload website.");
                    }
                }
            });
    });
};

export const ApiPostText = (type, userData) => {
    return new Promise((resolve, reject) => {
        axiosInstance
            .post(type, userData, {
                headers: {
                    Authorization: localStorage.getItem("auth_token"),
                },
                responseType: "text",
            })
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (error?.cancelled) {
                    return resolve({ cancelled: true });
                }
                reject(error);
            });
    });
};

export const ApiDelete = (type, userData) => {
    return new Promise((resolve, reject) => {
        axiosInstance
            .delete(type, {
                data: userData,
                headers: {
                    Authorization: localStorage.getItem("auth_token"),
                },
            })
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (error?.cancelled) {
                    return resolve({ cancelled: true });
                }
                handleAuthApiError(error, reject);
            });
    });
};

export const ApiPut = (type, userData) => {
    return new Promise((resolve, reject) => {
        axiosInstance
            .put(type, userData, {
                headers: {
                    Authorization: localStorage.getItem("auth_token"),
                },
            })
            .then((responseJson) => {
                resolve(responseJson);
            })
            .catch((error) => {
                if (error?.cancelled) {
                    return resolve({ cancelled: true });
                }
                handleAuthApiError(error, reject);
            });
    });
};
